import type { WireSourceInstance, WireUser } from '@logistics/contracts';

/** Every source, including ones connected later — or only the ticked ones. */
export type SourceMode = 'all' | 'only';

export interface KindGroup {
  readonly kind: string;
  readonly items: readonly WireSourceInstance[];
}

/** Instances under their plugin, in the order the API listed them. */
export function groupByKind(instances: readonly WireSourceInstance[]): KindGroup[] {
  const groups = new Map<string, WireSourceInstance[]>();
  for (const instance of instances) {
    const list = groups.get(instance.kind) ?? [];
    list.push(instance);
    groups.set(instance.kind, list);
  }
  return [...groups].map(([kind, items]) => ({ kind, items }));
}

/** The kind's checkbox: ticks every account of it, or clears them all if all were ticked. */
export function toggleKind(
  sourceIds: readonly string[],
  items: readonly WireSourceInstance[],
): string[] {
  const ids = items.map((i) => i.sourceId);
  const all = ids.every((id) => sourceIds.includes(id));
  return all
    ? sourceIds.filter((id) => !ids.includes(id))
    : [...sourceIds, ...ids.filter((id) => !sourceIds.includes(id))];
}

/** What the API is sent: `null` for every source. */
export function toSourceAccess(mode: SourceMode, sourceIds: readonly string[]): string[] | null {
  return mode === 'all' ? null : [...new Set(sourceIds)];
}

/** The SOURCES cell: "All", or how many were granted. */
export function sourcesCell(user: WireUser): string {
  if (user.role === 'admin' || user.sourceAccess === undefined) return 'All';
  const n = user.sourceAccess.length;
  return n === 0 ? 'None' : `${n} source${n === 1 ? '' : 's'}`;
}
